import React, { useState, useEffect, useRef } from 'react'
import { Bell, X, CheckCircle, AlertCircle, Info, Trash2 } from 'lucide-react'
import { Box, Flex, Text, Heading, Badge, Card, ScrollArea, IconButton } from '@radix-ui/themes'
import { useNotifications } from '../contexts/NotificationContext'

const NotificationBell: React.FC = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead, removeNotification, clearAll } = useNotifications()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />
      case 'info':
        return <Info className="w-4 h-4 text-blue-500" />
      default:
        return <Info className="w-4 h-4 text-gray-500" />
    }
  }

  const formatTimeAgo = (timestamp: Date) => {
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - timestamp.getTime()) / (1000 * 60))

    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`
    return `${Math.floor(diffInMinutes / 1440)}d ago`
  }

  const handleNotificationClick = (notification: any) => {
    if (!notification.read) {
      markAsRead(notification.id)
    }
    if (notification.action) {
      notification.action.onClick()
    }
    setIsOpen(false)
  }

  return (
    <Box style={{ position: 'relative', display: 'inline-flex' }} ref={dropdownRef as any}>
      <IconButton
        variant="ghost"
        size="2"
        onClick={() => setIsOpen(!isOpen)}
        style={{ position: 'relative' }}
      >
        <Bell className="w-5 h-5 text-gray-500" />
        {unreadCount > 0 && (
          <Badge
            color="red"
            style={{
              position: 'absolute',
              top: '-2px',
              insetInlineEnd: '-2px',
              backgroundColor: 'var(--red-9)',
              color: 'white',
              boxShadow: '0 0 0 2px var(--color-page-background)',
              minWidth: '20px',
              height: '20px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px'
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </IconButton>

      {isOpen && (
        <Card
          style={{
            position: 'absolute',
            insetInlineEnd: '-10px',
            top: '100%',
            marginTop: '10px',
            width: '320px',
            zIndex: 50,
            boxShadow: 'var(--shadow-5)'
          }}
        >
          <Box p="4" style={{ borderBottom: '1px solid var(--gray-6)' }}>
            <Flex align="center" justify="between">
              <Heading size="4">Notifications</Heading>
              <Flex align="center" gap="2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    style={{
                      fontSize: '12px',
                      padding: '4px 8px',
                      borderRadius: 'var(--radius-2)',
                      border: 'none',
                      backgroundColor: 'transparent',
                      color: 'var(--gray-11)',
                      cursor: 'pointer'
                    }}
                    className="hover:bg-[var(--gray-3)]"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={clearAll}
                  style={{
                    fontSize: '12px',
                    padding: '4px 8px',
                    borderRadius: 'var(--radius-2)',
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: 'var(--red-11)',
                    cursor: 'pointer'
                  }}
                  className="hover:bg-[var(--gray-3)]"
                >
                  Clear all
                </button>
                <IconButton
                  variant="ghost"
                  size="1"
                  onClick={() => setIsOpen(false)}
                >
                  <X className="w-4 h-4" />
                </IconButton>
              </Flex>
            </Flex>
          </Box>

          <ScrollArea style={{ maxHeight: '384px' }}>
            {notifications.length === 0 ? (
              <Flex direction="column" align="center" justify="center" p="8">
                <Bell className="w-8 h-8 mb-2" style={{ opacity: 0.5 }} />
                <Text color="gray">No notifications</Text>
              </Flex>
            ) : (
              <Box p="2">
                {notifications.map((notification) => (
                  <Card
                    key={notification.id}
                    style={{
                      marginBottom: '8px',
                      cursor: 'pointer',
                      backgroundColor: !notification.read ? 'var(--blue-3)' : undefined
                    }}
                    onClick={() => handleNotificationClick(notification)}
                    className={!notification.read ? '' : 'hover:bg-[var(--gray-3)]'}
                  >
                    <Box p="3">
                      <Flex gap="3" align="start">
                        <Box style={{ flexShrink: 0, marginTop: '2px' }}>
                          {getNotificationIcon(notification.type)}
                        </Box>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Flex align="start" justify="between">
                            <Box style={{ flex: 1 }}>
                              <Text size="2" weight="medium" as="div" mb="1">
                                {notification.title}
                              </Text>
                              <Text size="1" color="gray" as="div" mb="2">
                                {notification.message}
                              </Text>
                              <Text size="1" color="gray">
                                {formatTimeAgo(notification.timestamp)}
                              </Text>
                            </Box>
                            <IconButton
                              variant="ghost"
                              size="1"
                              onClick={(e) => {
                                e.stopPropagation()
                                removeNotification(notification.id)
                              }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </IconButton>
                          </Flex>
                          {notification.action && (
                            <button
                              style={{
                                marginTop: '8px',
                                fontSize: '12px',
                                padding: '4px 8px',
                                border: '1px solid var(--gray-6)',
                                borderRadius: 'var(--radius-2)',
                                backgroundColor: 'transparent',
                                color: 'var(--gray-12)',
                                cursor: 'pointer'
                              }}
                              className="hover:bg-[var(--gray-3)]"
                              onClick={(e) => {
                                e.stopPropagation()
                                notification.action?.onClick()
                              }}
                            >
                              {notification.action.label}
                            </button>
                          )}
                        </Box>
                      </Flex>
                    </Box>
                  </Card>
                ))}
              </Box>
            )}
          </ScrollArea>
        </Card>
      )}
    </Box>
  )
}

export default NotificationBell 